/**
 * Version Approval Workflow
 * Manages version approval states and workflow configuration
 */

import React, { useState, useEffect } from 'react';
import { DocumentVersion } from '../../../shared/document/types';
import { DocumentService } from '../../../shared/document/DocumentService';
import { 
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  EyeIcon,
  PencilIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftIcon,
} from '@heroicons/react/24/outline';

interface VersionApprovalWorkflowProps {
  documentId: string;
  currentVersion: DocumentVersion;
  onApprovalChange?: (version: DocumentVersion, newStatus: ApprovalStatus) => void;
}

export type ApprovalStatus = 'draft' | 'review' | 'approved' | 'rejected';

interface ApprovalState {
  status: ApprovalStatus;
  reviewer?: string;
  reviewDate?: Date;
  comments?: string;
  workflowStep: number;
}

interface ApprovalWorkflow {
  steps: ApprovalWorkflowStep[];
  currentStep: number;
  isComplete: boolean;
}

interface ApprovalWorkflowStep {
  id: string;
  name: string;
  description: string;
  requiredRole: string;
  isComplete: boolean;
  completedBy?: string;
  completedAt?: Date;
}

export function VersionApprovalWorkflow({
  documentId,
  currentVersion,
  onApprovalChange,
}: VersionApprovalWorkflowProps): React.JSX.Element {
  const [approvalState, setApprovalState] = useState<ApprovalState>({
    status: 'draft',
    workflowStep: 0,
  });
  const [workflow, setWorkflow] = useState<ApprovalWorkflow>({
    steps: [
      {
        id: 'draft',
        name: 'Draft',
        description: 'Document is in draft state',
        requiredRole: 'author',
        isComplete: true,
      },
      {
        id: 'review',
        name: 'Review',
        description: 'Document is under review',
        requiredRole: 'reviewer',
        isComplete: false,
      },
      {
        id: 'approval',
        name: 'Approval',
        description: 'Document requires final approval',
        requiredRole: 'approver',
        isComplete: false,
      },
    ],
    currentStep: 0,
    isComplete: false,
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState('');

  useEffect(() => {
    loadApprovalState();
  }, [documentId, currentVersion]);

  const loadApprovalState = async () => {
    try {
      // Load approval state from document metadata
      const document = await DocumentService.getDocumentById(documentId);
      const metadata = document.metadata || {};
      
      if (metadata.approvalState) {
        setApprovalState(metadata.approvalState);
      }
      
      if (metadata.approvalWorkflow) {
        setWorkflow(metadata.approvalWorkflow);
      }
    } catch (error) {
      console.error('Failed to load approval state:', error);
    }
  };

  const updateApprovalStatus = async (newStatus: ApprovalStatus, reviewer?: string) => {
    setIsProcessing(true);
    try {
      const updatedState: ApprovalState = {
        ...approvalState,
        status: newStatus,
        reviewer: reviewer || 'Current User',
        reviewDate: new Date(),
        workflowStep: getWorkflowStep(newStatus),
      };

      // Update document metadata
      await DocumentService.saveLexicalContent(documentId, '', {
        approvalState: updatedState,
        approvalWorkflow: updateWorkflowProgress(newStatus),
      });

      setApprovalState(updatedState);
      onApprovalChange?.(currentVersion, newStatus);
    } catch (error) {
      console.error('Failed to update approval status:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getWorkflowStep = (status: ApprovalStatus): number => {
    switch (status) {
      case 'draft': return 0;
      case 'review': return 1;
      case 'approved': return 2;
      case 'rejected': return 0; // Back to draft
      default: return 0;
    }
  };

  const updateWorkflowProgress = (status: ApprovalStatus): ApprovalWorkflow => {
    const updatedSteps = [...workflow.steps];
    
    switch (status) {
      case 'draft':
        updatedSteps[0].isComplete = true;
        updatedSteps[1].isComplete = false;
        updatedSteps[2].isComplete = false;
        break;
      case 'review':
        updatedSteps[0].isComplete = true;
        updatedSteps[1].isComplete = true;
        updatedSteps[1].completedBy = 'Current User';
        updatedSteps[1].completedAt = new Date();
        updatedSteps[2].isComplete = false;
        break;
      case 'approved':
        updatedSteps[0].isComplete = true;
        updatedSteps[1].isComplete = true;
        updatedSteps[2].isComplete = true;
        updatedSteps[2].completedBy = 'Current User';
        updatedSteps[2].completedAt = new Date();
        break;
      case 'rejected':
        updatedSteps[0].isComplete = true;
        updatedSteps[1].isComplete = false;
        updatedSteps[2].isComplete = false;
        break;
    }

    return {
      ...workflow,
      steps: updatedSteps,
      currentStep: getWorkflowStep(status),
      isComplete: status === 'approved',
    };
  };

  const getStatusIcon = (status: ApprovalStatus) => {
    switch (status) {
      case 'draft':
        return <PencilIcon className="h-5 w-5 text-gray-500" />;
      case 'review':
        return <EyeIcon className="h-5 w-5 text-blue-500" />;
      case 'approved':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'rejected':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: ApprovalStatus) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      case 'review':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'approved':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const canTransitionTo = (targetStatus: ApprovalStatus): boolean => {
    switch (approvalState.status) {
      case 'draft':
        return targetStatus === 'review';
      case 'review':
        return targetStatus === 'approved' || targetStatus === 'rejected';
      case 'approved':
        return false; // Cannot change once approved
      case 'rejected':
        return targetStatus === 'draft';
      default:
        return false;
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <ShieldCheckIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Approval Workflow
            </h3>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(approvalState.status)}`}>
            <div className="flex items-center space-x-2">
              {getStatusIcon(approvalState.status)}
              <span className="capitalize">{approvalState.status}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Workflow Steps */}
      <div className="p-4">
        <div className="space-y-4">
          {workflow.steps.map((step, index) => (
            <div key={step.id} className="flex items-start space-x-4">
              {/* Step Indicator */}
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step.isComplete
                  ? 'bg-green-500 text-white'
                  : index === workflow.currentStep
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
              }`}>
                {step.isComplete ? (
                  <CheckCircleIcon className="h-5 w-5" />
                ) : (
                  index + 1
                )}
              </div>

              {/* Step Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {step.name}
                  </h4>
                  {step.isComplete && step.completedBy && (
                    <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                      <UserIcon className="h-3 w-3" />
                      <span>{step.completedBy}</span>
                    </div>
                  )}
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {step.description}
                </p>
                {step.completedAt && (
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    Completed {step.completedAt.toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowComments(!showComments)}
              className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
            >
              <ChatBubbleLeftIcon className="h-4 w-4" />
              <span>Add Comments</span>
            </button>
          </div>

          <div className="flex items-center space-x-2">
            {/* Status Transition Buttons */}
            {canTransitionTo('review') && (
              <button
                onClick={() => updateApprovalStatus('review')}
                disabled={isProcessing}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Submit for Review'}
              </button>
            )}

            {canTransitionTo('approved') && (
              <button
                onClick={() => updateApprovalStatus('approved')}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Approve'}
              </button>
            )}

            {canTransitionTo('rejected') && (
              <button
                onClick={() => updateApprovalStatus('rejected')}
                disabled={isProcessing}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Reject'}
              </button>
            )}

            {canTransitionTo('draft') && (
              <button
                onClick={() => updateApprovalStatus('draft')}
                disabled={isProcessing}
                className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Processing...' : 'Return to Draft'}
              </button>
            )}
          </div>
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="mt-4">
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add review comments..."
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={() => {
                  // Save comments logic here
                  setShowComments(false);
                }}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                Save Comments
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default VersionApprovalWorkflow; 